import {
  FccAdAlertType,
  TaskNotificationStatus,
  TaskNotificationType,
} from '@backoffice-frontend/graphql'
import type { ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import type { AlertFilterTypes } from '../const'
import { AssignedToMeFilter } from '../const'
import type { AlertSeverity } from '../utils'

export const getAlertVariant = (severity?: AlertSeverity) => {
  switch (severity) {
    case 'Highest':
      return 'critical'
    case 'High':
      return 'warning'
    default:
      return 'neutral'
  }
}

export const useTaskTypeTranslation = () => {
  const { t } = useTranslation()
  const map: Partial<Record<TaskNotificationType | FccAdAlertType, string>> = {
    // AD alerts
    [FccAdAlertType.PassengerUnbuckled]: t(
      'fleetMap_alertTypePassengerUnbuckled_label',
    ),
    [FccAdAlertType.CabinToOperatorCall]: t(
      'fleetMap_alertTypeCabinToOperatorCall_label',
    ),
    [FccAdAlertType.OperatorToCabinCall]: t(
      'fleetMap_alertTypeOperatorToCabinCall_label',
    ),
    [FccAdAlertType.EmergencyStop]: t('fleetMap_alertTypeEmergencyStop_label'),
    [FccAdAlertType.ManualNoShow]: t('fleetMap_alertTypeManualNoShow_label'),
    [FccAdAlertType.WaypointAttendance]: t(
      'fleetMap_alertTypeWaypointAttendance_label',
    ),
    [FccAdAlertType.ServiceCancellationOngoing]: t(
      'fleetMap_alertTypeServiceCancellationOngoing_label',
    ),
    [FccAdAlertType.CabinSafetyCheck]: t(
      'fleetMap_alertTypeCabinSafetyCheck_label',
    ),
    [FccAdAlertType.SafetyDriverCalling]: t(
      'fleetMap_alertTypeIncomingSafetyDriverCall_label',
    ),
    [FccAdAlertType.OutgoingOperatorToSafetyDriverCall]: t(
      'fleetMap_alertTypeOutgoingOperatorToSafetyDriverCall_label',
    ),
    [FccAdAlertType.IncomingSafetyDriverCall]: t(
      'fleetMap_alertTypeIncomingSafetyDriverCall_label',
    ),
    [FccAdAlertType.TripCancellationOngoing]: t(
      'fleetMap_alertTypeTripCancellationOngoing_label',
    ),
    [FccAdAlertType.VehicleGuidanceRequested]: t(
      'adFleetMap_alertTypeManeuverApproval_label',
    ),
    // Task notifications
    [TaskNotificationType.SamRoadBlockageReported]: t(
      'fleetMap_alertTypeSamRoadBlockageReported_label',
    ),
    [TaskNotificationType.IvcCameraErrorCameraNotReachable]: t(
      'fleetMap_alertTypeIvcCameraErrorCameraNotReachable_label',
    ),
    [TaskNotificationType.IvcCameraErrorCameraRecordingsNotChecked]: t(
      'fleetMap_alertTypeIvcCameraErrorCameraRecordingsNotChecked_label',
    ),
    [TaskNotificationType.IvcCameraErrorCameraResponseTypeError]: t(
      'fleetMap_alertTypeIvcCameraErrorCameraRecordingsNotChecked_label',
    ),
    [TaskNotificationType.IvcCameraErrorCameraSdCardNotFound]: t(
      'fleetMap_alertTypeIvcCameraErrorCameraSdCardNotFound_label',
    ),
    [TaskNotificationType.IvcCameraErrorCameraSdCardSizeMismatch]: t(
      'fleetMap_alertTypeIvcCameraErrorCameraSdCardSizeMismatch_label',
    ),
    [TaskNotificationType.IvcCameraErrorCameraWebserverNotReachable]: t(
      'fleetMap_alertTypeIvcCameraErrorCameraWebserverNotReachable_label',
    ),
    [TaskNotificationType.IvcCameraErrorNoMatchingRecordingsFound]: t(
      'fleetMap_alertTypeIvcCameraErrorNoMatchingRecordingsFound_label',
    ),
    [TaskNotificationType.ShiftTimesShiftTypeChangeNotification]: t(
      'fleetMap_alertTypeShiftTimesShiftTypeChange_label',
    ),
    [TaskNotificationType.IvcCameraErrorNoTimedateFromCamera]: t(
      'fleetMap_alertTypeIvcCameraErrorNoTimedateFromCamera_label',
    ),
    [TaskNotificationType.IvcCameraErrorNtpNotConfigured]: t(
      'fleetMap_alertTypeIvcCameraErrorNtpNotConfigured_label',
    ),
    [TaskNotificationType.IvcCdisErrorHostUnreachable]: t(
      'fleetMap_alertTypeIvcCdisErrorHostUnreachable_label',
    ),
    [TaskNotificationType.IvcCdisErrorTimedOut]: t(
      'fleetMap_alertTypeIvcCdisErrorTimedOut_label',
    ),
    [TaskNotificationType.IvcCdisNotReachable]: t(
      'fleetMap_alertTypeIvcCdisNotReachable_label',
    ),
    [TaskNotificationType.IvcCdisNoMapAvailable]: t(
      'fleetMap_alertTypeIvcCdisNoMapAvailable_label',
    ),
    [TaskNotificationType.IvcInternetNotReachable]: t(
      'fleetMap_alertTypeIvcInternetNotReachable_label',
    ),
    [TaskNotificationType.IvcRouterNotReachable]: t(
      'fleetMap_alertTypeIvcRouterNotReachable_label',
    ),
    [TaskNotificationType.IvcRouterOffline]: t(
      'fleetMap_alertTypeIvcRouterOffline_label',
    ),
    [TaskNotificationType.ShiftTimesInvalidBreakNotification]: t(
      'fleetMap_alertTypeShiftTimesInvalidBreak_label',
    ),
    [TaskNotificationType.ShiftTimesMissingBreakEndNotification]: '',
    [TaskNotificationType.ShiftTimesMissingClockEventNotification]: t(
      'fleetMap_alertTypeShiftTimesMissingClockEvent_label',
    ),
    [TaskNotificationType.VehicleOutOfServiceAreaNotification]: t(
      'fleetMap_alertTypeVehicleOutOfServiceArea_label',
    ),
    [TaskNotificationType.VehicleNoBookingNotification]: t(
      'fleetMap_alertTypeVehicleNoBooking_label',
    ),
    [TaskNotificationType.ShiftPoolingOffDepartureNotification]: t(
      'fleetMap_alertTypeShiftPoolingOffDeparture_label',
    ),
    [TaskNotificationType.ShiftMissingBreakStartedNotification]: t(
      'fleetMap_alertTypeShiftMissingBreakStarted_label',
    ),
    [TaskNotificationType.ShiftMissingHubCheckinNotification]: t(
      'fleetMap_alertTypeShiftMissingHubCheckin_label',
    ),
    [TaskNotificationType.ShiftMissingHubCheckoutNotification]: t(
      'fleetMap_alertTypeShiftMissingHubCheckout_label',
    ),
    [TaskNotificationType.ShiftDrivingWithoutBreakNotification]: t(
      'fleetMap_alertTypeShiftDrivingWithoutBreak_label',
    ),
    [TaskNotificationType.ShiftDeviationNotification]: '',
    [TaskNotificationType.StopValidationFailed]: t(
      'fleetMap_alertTypeStopValidationFailed_label',
    ),
    [TaskNotificationType.TripPrebookingNoAssignment]: t(
      'fleetMap_alertTypeTripPrebookingNoAssignment_label',
    ),
    [TaskNotificationType.VehicleCdisInterruptedNotification]: t(
      'fleetMap_alertTypeVehicleCdisInterrupted_label',
    ),
    [TaskNotificationType.Vehicle_12VBatteryCriticalNotification]: t(
      'fleetMap_alertType12VBatteryCritical_label',
    ),
    [TaskNotificationType.VehicleChargingErrorNotification]: t(
      'fleetMap_alertTypeVehicleChargingError_label',
    ),
    [TaskNotificationType.VehicleChargingNeededNotification]: t(
      'fleetMap_alertTypeVehicleChargingNeeded_label',
    ),
    [TaskNotificationType.VehicleNoHubReturnNotification]: t(
      'fleetMap_alertTypeVehicleNoHubReturn_label',
    ),
    [TaskNotificationType.VehicleWithoutDriverNotification]: t(
      'fleetMap_alertTypeVehicleWithoutDriver_label',
    ),
    [TaskNotificationType.VehicleLowStateOfChargeNotification]: t(
      'fleetMap_alertTypeVehicleLowStateOfCharge_label',
    ),
    [TaskNotificationType.FcVehicleCabinSafetyCheckRequiredNotification]: t(
      'fleetMap_alertTypeCabinSafetyCheck_label',
    ),
    [TaskNotificationType.FcVehicleOutOfServiceNotification]: t(
      'fleetMap_alertTypeFcVehicleOutOfService_label',
    ),
    [TaskNotificationType.FcPassengerAuthenticationFallbackRequestNotification]:
      t('fleetMap_alertTypeFcPassengerAuthenticationFallbackRequest_label'),
  }

  return (key?: TaskNotificationType | FccAdAlertType): string =>
    (key && map[key]) ?? t('fleetMap_alertTypeUnknown_label')
}

export const useTaskInfoTranslation = () => {
  const { t } = useTranslation()
  const map: Partial<Record<TaskNotificationType | FccAdAlertType, ReactNode>> =
    {
      // AD alerts
      [FccAdAlertType.PassengerUnbuckled]: t(
        'fleetMap_alertTypePassengerUnbuckled_tooltip',
      ),
      // TaskNotifications
      [TaskNotificationType.VehicleOutOfServiceAreaNotification]: (
        <Trans components={{ ul: <ul />, li: <li /> }}>
          {t('fleetMap_alertTypeVehicleOutOfServiceArea_tooltip')}
        </Trans>
      ),
      [TaskNotificationType.VehicleNoBookingNotification]: (
        <Trans components={{ ul: <ul />, li: <li /> }}>
          {t('fleetMap_alertTypeVehicleNoBooking_tooltip')}
        </Trans>
      ),
      [TaskNotificationType.TripPrebookingNoAssignment]: t(
        'fleetMap_alertTypeTripPrebookingNoAssignment_tooltip',
      ),
      [TaskNotificationType.VehicleNoHubReturnNotification]: (
        <Trans components={{ ul: <ul />, li: <li /> }}>
          {t('fleetMap_alertTypeVehicleNoHubReturn_tooltip')}
        </Trans>
      ),
      [TaskNotificationType.VehicleWithoutDriverNotification]: (
        <Trans components={{ ul: <ul />, li: <li /> }}>
          {t('fleetMap_alertTypeVehicleWithoutDriver_tooltip')}
        </Trans>
      ),
      [TaskNotificationType.VehicleLowStateOfChargeNotification]: (
        <Trans components={{ ul: <ul />, li: <li /> }}>
          {t('fleetMap_alertTypeVehicleLowStateOfCharge_tooltip')}
        </Trans>
      ),
      [TaskNotificationType.FcVehicleCabinSafetyCheckRequiredNotification]: (
        <Trans components={{ br: <br /> }}>
          {t('fleetMap_alertTypeFcVehicleCabinSafetyCheckRequired_tooltip')}
        </Trans>
      ),
      [TaskNotificationType.FcVehicleOutOfServiceNotification]: (
        <Trans components={{ ul: <ul />, li: <li /> }}>
          {t('fleetMap_alertTypeFcVehicleOutOfService_tooltip')}
        </Trans>
      ),
      [TaskNotificationType.FcPassengerAuthenticationFallbackRequestNotification]:
        t('fleetMap_alertTypeFcPassengerAuthenticationFallbackRequest_tooltip'),
      [TaskNotificationType.ShiftDrivingWithoutBreakNotification]: t(
        'fleetMap_alertTypeShiftDrivingWithoutBreak_tooltip',
      ),
      [TaskNotificationType.ShiftMissingHubCheckinNotification]: t(
        'fleetMap_alertTypeShiftMissingHubCheckin_tooltip',
      ),
    }

  return (key: TaskNotificationType | FccAdAlertType) => map[key] ?? null
}

export const useTaskNotificationStatusLabel = () => {
  const { t } = useTranslation()

  return (key: TaskNotificationStatus) => {
    if (!key) return ''

    const map: Record<TaskNotificationStatus, string> = {
      [TaskNotificationStatus.New]: t('fleetMap_alertStatusNew_label'),
      [TaskNotificationStatus.InProgress]: t(
        'fleetMap_alertStatusInProgress_label',
      ),
      [TaskNotificationStatus.Pending]: t('fleetMap_alertStatusPending_label'),
      [TaskNotificationStatus.Solved]: t('fleetMap_alertStatusSolved_label'),
    }

    return map[key]
  }
}

export const useTaskTableColumn = () => {
  const { t } = useTranslation()

  return (key: string) => {
    if (!key) return ''

    const map: Record<string, string> = {
      'Drop off stop': t('fleetMap_columnDropOffStop_header'),
      'Pick up stop': t('fleetMap_columnPickUpStop_header'),
      'Time left': t('fleetMap_columnTimeLeft_header'),
      'Vehicle Location': t('fleetMap_columnVehicleLocation_header'),
      Action: t('fleetMap_columnAction_header'),
      Alert: t('fleetMap_columnAlert_header'),
      Assigned: t('fleetMap_columnAssigned_header'),
      Created: t('fleetMap_columnCreated_header'),
      Customer: t('fleetMap_columnCustomer_header'),
      Driver: t('fleetMap_columnDriver_header'),
      Entity: t('fleetMap_columnEntity_header'),
      Resolved: t('fleetMap_columnResolved_header'),
      Requirement: t('fleetMap_columnRequirement_header'),
      State: t('fleetMap_columnState_header'),
      Vehicle: t('fleetMap_columnVehicle_header'),
      'Last Occurred': t('fleetMap_columnLastOccurred_header'),
    }
    return map[key]
  }
}

export const useAlertsFilter = () => {
  const { t } = useTranslation()

  return (key: AlertFilterTypes) => {
    if (!key) return ''

    const map: Record<
      TaskNotificationStatus | typeof AssignedToMeFilter,
      string
    > = {
      [TaskNotificationStatus.New]: t('fleetMap_alertFilterNew_label'),
      [AssignedToMeFilter]: t('fleetMap_alertFilterMyInProgress_label'),
      [TaskNotificationStatus.InProgress]: t(
        'fleetMap_alertFilterInProgress_label',
      ),
      [TaskNotificationStatus.Pending]: t('fleetMap_alertFilterPending_label'),
      [TaskNotificationStatus.Solved]: t('fleetMap_alertFilterSolved_label'),
    }
    return map[key]
  }
}
